import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Button,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import VerifiedIcon from "@mui/icons-material/Verified";
import HubIcon from "@mui/icons-material/Hub";
import SpeedIcon from "@mui/icons-material/Speed";
import GroupsIcon from "@mui/icons-material/Groups";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar/Navbar";
import Footer from "../components/Footer/Footer";

const themeColor = "#286a94";

const Demo = () => {
  const navigate = useNavigate();

  return (
    <>
      <Navbar />

      {/* ---------------- Hero Banner ---------------- */}
      <Box
        sx={{
          width: "100%",
          py: { xs: 6, md: 10 },
          px: 2,
          background: `linear-gradient(135deg, #1c4561, #286a94)`,
          color: "white",
          textAlign: "center",
        }}
      >
        <Typography
          variant="h3"
          sx={{
            fontWeight: 800,
            mb: 2,
            fontSize: { xs: "26px", md: "40px" },
          }}
        >
          Schedule a Live Product Demo
        </Typography>

        <Typography
          sx={{
            maxWidth: "750px",
            mx: "auto",
            fontSize: { xs: "14px", md: "16px" },
            lineHeight: 1.6,
            opacity: 0.92,
          }}
        >
          See how eBench.ai helps hiring teams automate, accelerate, and optimize
          recruitment using AI-driven workflows and skill-based talent intelligence.
        </Typography>

        <Button
          variant="contained"
          size="large"
          onClick={() => navigate("/contact-us")}
          sx={{
            mt: 4,
            px: 5,
            py: 1.3,
            background: "#ffffff",
            color: themeColor,
            borderRadius: "10px",
            fontWeight: 600,
            "&:hover": { background: "#f4f7fa" },
          }}
          startIcon={<CalendarMonthIcon />}
        >
          Book Demo
        </Button>
      </Box>

      {/* ---------------- What Will You See ---------------- */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Typography
          variant="h4"
          sx={{
            textAlign: "center",
            fontWeight: 700,
            color: themeColor,
            mb: 4,
          }}
        >
          What will you see in the demo?
        </Typography>

        <Grid container spacing={4}>
          {[
            {
              icon: <VisibilityIcon sx={{ fontSize: 50, color: themeColor }} />,
              title: "Platform Walkthrough",
              desc: "Explore the dashboard, job workflows, skill profiles, and analytics tools.",
            },
            {
              icon: (
                <SupportAgentIcon sx={{ fontSize: 50, color: themeColor }} />
              ),
              title: "Live Feature Testing",
              desc: "Hands-on experience with automation, screening, matching, and shortlisting.",
            },
            {
              icon: (
                <RocketLaunchIcon sx={{ fontSize: 50, color: themeColor }} />
              ),
              title: "Tailored Use-Case Setup",
              desc: "Your workflow mapped to eBench.ai — enterprise, talent partner, or staffing.",
            },
          ].map((item, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Paper
                elevation={4}
                sx={{
                  p: 4,
                  borderRadius: "14px",
                  height: "100%",
                  textAlign: "center",
                  borderTop: `6px solid ${themeColor}`,
                  transition: "0.3s",
                  "&:hover": { transform: "translateY(-4px)", boxShadow: 6 },
                }}
              >
                {item.icon}
                <Typography variant="h6" sx={{ fontWeight: 700, mt: 2 }}>
                  {item.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ mt: 1, color: "#616161", lineHeight: 1.6 }}
                >
                  {item.desc}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* ---------------- Why Book Section ---------------- */}
        <Box sx={{ mt: 8 }}>
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, color: themeColor, textAlign: "center", mb: 4 }}
          >
            Why book a demo?
          </Typography>

          <Grid container spacing={4}>
            {[
              {
                icon: <SpeedIcon sx={{ fontSize: 50, color: themeColor }} />,
                title: "Faster Hiring Cycles",
                desc: "Reduce time-to-hire by automating screening, matching, and interview processes.",
              },
              {
                icon: <VerifiedIcon sx={{ fontSize: 50, color: themeColor }} />,
                title: "Skill-Based Accuracy",
                desc: "Make hiring decisions based on verified skills and performance potential — not keywords.",
              },
              {
                icon: <HubIcon sx={{ fontSize: 50, color: themeColor }} />,
                title: "All-in-One Workflow",
                desc: "A unified platform for tracking roles, candidates, communication, and hiring decisions.",
              },
            ].map((item, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 4,
                    borderRadius: "16px",
                    textAlign: "center",
                    transition: "0.3s",
                    "&:hover": { transform: "scale(1.03)", boxShadow: 6 },
                  }}
                >
                  {item.icon}
                  <Typography variant="h6" sx={{ fontWeight: 700, mt: 2 }}>
                    {item.title}
                  </Typography>
                  <Typography sx={{ mt: 1, color: "#616161", lineHeight: 1.6 }}>
                    {item.desc}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* ---------------- Who Is It For ---------------- */}
        <Box sx={{ mt: 8 }}>
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, color: themeColor, textAlign: "center", mb: 4 }}
          >
            Who is this demo designed for?
          </Typography>

          <Grid container spacing={4}>
            {[
              {
                icon: <GroupsIcon sx={{ fontSize: 50, color: themeColor }} />,
                label: "Hiring Managers",
              },
              {
                icon: <LightbulbIcon sx={{ fontSize: 50, color: themeColor }} />,
                label: "Talent Acquisition Teams",
              },
              {
                icon: <BusinessCenterIcon sx={{ fontSize: 50, color: themeColor }} />,
                label: "Staffing & HR Agencies",
              },
              {
                icon: <RocketLaunchIcon sx={{ fontSize: 50, color: themeColor }} />,
                label: "High-Growth Startups",
              },
            ].map((item, i) => (
              <Grid item xs={6} md={3} key={i}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 3,
                    textAlign: "center",
                    borderRadius: "12px",
                    "&:hover": { boxShadow: 6, transform: "translateY(-4px)" },
                  }}
                >
                  {item.icon}
                  <Typography sx={{ fontWeight: 600, mt: 1 }}>{item.label}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* ---------------- CTA Section ---------------- */}
        <Box
          sx={{
            textAlign: "center",
            mt: 8,
            p: 4,
            background: "#f4faff",
            borderRadius: "16px",
            border: `1px solid ${themeColor}22`,
          }}
        >
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, color: themeColor, mb: 2 }}
          >
            Ready to See eBench.ai in Action?
          </Typography>

          <Typography
            variant="body2"
            sx={{
              maxWidth: "650px",
              mx: "auto",
              mb: 4,
              color: "#555",
            }}
          >
            Get a personalized walkthrough, ask live questions, and explore how AI-based hiring 
            will streamline workforce scaling and improve talent decisions.
          </Typography>

          <Box
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <Button
              variant="contained"
              sx={{
                background: themeColor,
                px: 4,
                py: 1.3,
                borderRadius: "10px",
                fontWeight: 600,
                "&:hover": { background: "#1d506e" },
              }}
              startIcon={<CalendarMonthIcon />}
              onClick={() => navigate("/contact-us")}
            >
              Schedule Demo
            </Button>

            <Button
              variant="outlined"
              sx={{
                borderColor: themeColor,
                color: themeColor,
                px: 4,
                py: 1.3,
                borderRadius: "10px",
                fontWeight: 600,
                "&:hover": { background: "#e3f2fa" },
              }}
              startIcon={<RocketLaunchIcon />}
              onClick={() => navigate("/register")}
            >
              Start Free Trial
            </Button>
          </Box>
        </Box>
      </Container>

      <Footer />
    </>
  );
};

export default Demo;
